import React, {useCallback} from 'react';
import {TouchableOpacity, View, ImageStyle} from 'react-native';

import Screen from '../../shared/components/screen';

import {IStateProps} from './model';
import {NamedStyles, StyleHelper, useStyles} from '../../shared/hooks/styles';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {Button} from '../../shared/components/button';
import {images} from '../../assets/icons';
import {DeviceSize} from '../../shared/theme/helpers';
import {stylePatterns} from '../../shared/theme/patterns';
import {Accordion} from '../../shared/components/accordion';
import {SupportServiceQuestionsStack} from './components/faq';
import {Text} from '../../shared/components/text';
import {Markdown} from '../../shared/components/markdown';
import {useSelector} from 'react-redux';
import {contentSelector} from '../../redux/selectors/resources';
import {LoadableContent} from '../../shared/components/loadableContent';
import {CustomScrollView} from '../../shared/components/scrollView';
import {formatPhone} from '../../shared/pipes';
import {openExternalPhone} from '../../shared/pipes/openExternalLinks';
import {Hr} from '../../shared/components/hr';
import {themeSelector} from '../../redux/selectors/runtime/theme';
import {VisImpStatus} from '../../shared/models/visuallyImpairedSettings';
import {UscType} from '../../shared/models/profile/uscType';

const phoneIconSize = 24;

export default ({onPressTopicsButton, questions, uscType}: IStateProps) => {
  const isDesktop = useIsDesktop();
  const content = useSelector(contentSelector());
  const styles = useStyles((helper) => createStyles(helper));
  const {Phone} = images;
  const themeSettings = useSelector(themeSelector());

  const dataSetBgGradient = {
    dataSet: {
      class: themeSettings.visuallyImpaired !== VisImpStatus.Active ? 'bg-gradient-small' : '',
    },
  };

  const renderPhoneBlock = (title: string, phone?: string, testIdPrefix?: string) => (
    <View testID={`${testIdPrefix}-container-id`} style={styles.phoneContainer}>
      <Text testID={`${testIdPrefix}-title-id`} style={styles.phoneTitle}>
        {title}:
      </Text>
      <TouchableOpacity
        testID={`${testIdPrefix}-button-id`}
        onPress={() => openExternalPhone(phone)}
        {...{dataSet: {class: 'link'}}}>
        <View testID={`${testIdPrefix}-phone-content-id`} style={styles.phoneContent}>
          <View style={styles.phoneContentIcon}>
            <Phone width={phoneIconSize} height={phoneIconSize} color={styles.phoneContentIcon.color} />
          </View>
          <Text testID={`${testIdPrefix}-phone-value-id`} style={styles.phoneContentText}>
            {formatPhone(phone)}
          </Text>
        </View>
      </TouchableOpacity>
    </View>
  );

  const phonesBlock = useCallback(() => {
    return (
      <React.Fragment>
        {renderPhoneBlock('Горячая линия Единой карты жителя', content.eskmoContacts.supportPhone, 'support-phone')}
        {uscType === UscType.Financial
          ? renderPhoneBlock('Горячая линия Совкомбанка', content.services.bankingService.bankPhone, 'bank-phone')
          : null}
      </React.Fragment>
    );
  }, [uscType, content.services.bankingService.bankPhone, content.eskmoContacts.supportPhone]);

  return (
    <Screen>
      <CustomScrollView contentInsetAdjustmentBehavior='automatic'>
        <View>
          <View style={styles.container}>
            <View style={styles.wrap}>
              <View style={styles.leftContainer}>
                {isDesktop ? (
                  <Text testID='suppoty-service-title-id' style={styles.sectionTitle}>
                    Служба поддержки
                  </Text>
                ) : null}
                <View style={styles.leftContainerTextWrapper}>
                  <Markdown>
                    {isDesktop ? content.support.descriptionDesktopMarkdown : content.support.descriptionMobileMarkdown}
                  </Markdown>
                </View>
              </View>
              <View style={styles.rightContainer}>
                {isDesktop ? (
                  <>
                    <View style={styles.cardButtonWrap}>
                      <Button
                        id='support-goto-topics'
                        style={styles.cardButton}
                        type='primary'
                        onPress={onPressTopicsButton}
                        text='Мои обращения'
                      />
                    </View>
                    <View style={styles.cardContainer}>
                      <View style={styles.cardBgWrap} {...dataSetBgGradient} />
                      <View style={styles.cardWrap}>
                        <View style={styles.cardContent}>{phonesBlock()}</View>
                      </View>
                    </View>
                  </>
                ) : (
                  <>
                    <View style={styles.cardButtonWrap}>
                      <Button
                        id='support-goto-topics'
                        style={styles.cardButton}
                        type='primary'
                        onPress={onPressTopicsButton}
                        text='Мои обращения'
                      />
                    </View>
                    {phonesBlock()}
                  </>
                )}
              </View>
            </View>
            {!isDesktop && <Hr marginTop={0} marginBottom={10} />}
            <View style={styles.questionsWrap}>
              <LoadableContent loading={!questions}>
                {questions?.length ? (
                  <Text testID='reference-info-id' style={styles.questionsTitle}>
                    Справочная информация
                  </Text>
                ) : null}
                {questions
                  ? questions.map((data, index) => (
                      <Accordion
                        index={index}
                        title={data.name}
                        key={`${data.name}${index}`}
                        contentCustomStyle={styles.questionsAccordionContent}>
                        <SupportServiceQuestionsStack questions={data.questions || []} />
                      </Accordion>
                    ))
                  : null}
              </LoadableContent>
            </View>
          </View>
        </View>
      </CustomScrollView>
    </Screen>
  );
};

const desktopCardWidth = 548;
const desktopCardBGWidth = 505;
const innerDesktopCardWidth = 500;

const createStyles = ({theme, isDesktop, mediaOnly, mediaExclude}: StyleHelper): NamedStyles<any> => ({
  container: {
    ...mediaOnly(DeviceSize.desktop, {marginTop: 32, paddingHorizontal: 24}, {marginTop: 28, paddingHorizontal: 16}),
    paddingBottom: theme.screen.bottomOffset - (isDesktop ? 30 : 20),
  },
  sectionTitle: {
    ...theme.fonts.h2,
    marginBottom: 40,
  },
  wrap: {
    display: 'flex',
    flexDirection: isDesktop ? 'row' : 'column',
  },
  leftContainer: {
    ...mediaOnly(DeviceSize.desktop, {flex: 1}, {width: '100%'}),
    paddingRight: isDesktop ? 28 : 0,
  },
  leftContainerTextWrapper: {
    marginBottom: isDesktop ? 30 : 20,
  },
  rightContainer: mediaOnly(
    DeviceSize.desktop,
    {width: desktopCardWidth, flexBasis: desktopCardWidth},
    {width: '100%'},
  ),
  cardContainer: {
    position: 'relative',
    justifyContent: 'center',
    marginBottom: 30,
    paddingBottom: 15,
    paddingTop: 15,
    minHeight: isDesktop ? 300 : 170,
    width: isDesktop ? desktopCardWidth : '100%',
  },
  cardBgWrap: {
    position: 'absolute',
    top: 0,
    height: '100%',
    width: isDesktop ? desktopCardBGWidth : '100%',
  },
  cardWrap: {
    minHeight: isDesktop ? 218 : 170,
    alignSelf: 'flex-start',
    borderRadius: 14,
    width: isDesktop ? innerDesktopCardWidth : '100%',
    backgroundColor: theme.supportService.cardBgColor,
  },
  cardContent: {
    paddingTop: 30,
    paddingHorizontal: 40,
    paddingBottom: 6,
    maxWidth: '100%',
    width: '100%',
    flexGrow: 1,
    alignSelf: 'center',
  },
  cardTitle: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h6, theme.fonts.bodySmall),
    marginBottom: 16,
  },
  cardPhoneLink: {
    marginBottom: 20,
  },
  cardPhone: {
    ...stylePatterns.flexCenterVertical,
    width: 240,
  },
  cardPhoneIcon: {
    color: theme.supportService.phoneColor,
  },
  cardPhoneText: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h4, theme.fonts.action),
    marginLeft: 13,
  },
  cardButtonWrap: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: isDesktop ? 'flex-end' : 'flex-start',
    alignSelf: isDesktop ? 'flex-start' : undefined,
    width: isDesktop ? desktopCardWidth : undefined,
    marginBottom: 30,
  },
  cardButton: mediaOnly(DeviceSize.desktop, {width: 240}, {flex: 1}),
  phoneContainer: {
    paddingBottom: 24,
    ...mediaOnly(DeviceSize.mobile, {
      display: 'flex',
      flexDirection: 'column',
    }),
  },
  phoneTitle: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h5, theme.fonts.action),
    marginBottom: isDesktop ? 12 : 14,
  },
  phoneContent: {
    ...stylePatterns.flexCenterVertical,
    ...mediaExclude(DeviceSize.desktop, {paddingLeft: 25}),
  },
  phoneContentIcon: {
    color: theme.supportService.phoneColor,
  },
  phoneContentText: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h3Light, theme.fonts.h6Light),
    paddingLeft: 13,
  },
  questionsWrap: {
    marginTop: 20,
  },
  questionsTitle: {
    ...mediaOnly(DeviceSize.desktop, theme.fonts.h3, theme.fonts.h6),
    paddingBottom: isDesktop ? 40 : 22,
  },
  questionsAccordionContent: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  sectionContainerBg: {
    position: 'absolute',
    zIndex: -1,
    width: 466,
    height: 479,
    right: -330,
    bottom: -124,
  },
  sectionContainerBgImage: {
    marginLeft: 'auto',
    marginBottom: 'auto',
    alignSelf: 'flex-start',
    color: theme.color.shaddowBg,
    width: 466,
    height: 479,
    resizeMode: 'contain',
  } as ImageStyle,
});
